import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { fetchCohorts, fetchFunnel, type CohortRow, type FunnelRow } from "../api";
import BackLink from "../components/BackLink";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { useSession } from "../session";

/**
 * AdminTraffic is how a post is measured (requirement 0063): per day and per
 * source and campaign, visits → visitors who came back within 7 days →
 * sign-ups, read from the launch funnel (ADR-0190). `sl api` reads the rest.
 *
 * Totals only, by design (ADR-0129): the server keeps nothing that names a
 * visitor, so there is nothing here to click into.
 */
export default function AdminTraffic() {
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;
  const traffic = useQuery({
    queryKey: ["admin", "funnel"],
    queryFn: fetchFunnel,
    enabled: isAdmin,
  });
  const cohorts = useQuery({
    queryKey: ["admin", "cohorts"],
    queryFn: fetchCohorts,
    enabled: isAdmin,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;

  const days = groupByDay(traffic.data?.items ?? []);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-8">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.traffic.title}
        </h1>
        <p className="mt-1 text-sm text-muted">{t.traffic.what}</p>
      </header>

      {(isPending || traffic.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}

      {traffic.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.traffic.loadFailed}{" "}
          <button type="button" onClick={() => void traffic.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}

      {traffic.data && days.length === 0 && (
        <p data-testid="traffic-empty" className="text-sm text-muted">
          {t.traffic.empty}
        </p>
      )}

      <div data-testid="traffic-days" className="space-y-6">
        {days.map(([day, rows]) => (
          <section key={day} data-testid="traffic-day">
            <h2 className="mb-2 text-sm font-semibold text-muted">{formatDay(day)}</h2>
            <table className="w-full rounded-card border border-line bg-surface text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-muted">
                  <th className="px-3 py-2 text-start font-medium">{t.traffic.source}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.traffic.visitors}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.traffic.returned}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.traffic.signups}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => (
                  <tr key={`${row.source}/${row.campaign}`} data-testid="traffic-row">
                    {/* A source is a tag or a host: Latin, and isolated so a
                        Persian interface does not flip its dots and dashes. */}
                    <td className="max-w-0 truncate px-3 py-2">
                      <bdi data-testid="traffic-source">
                        {row.campaign ? `${row.source} / ${row.campaign}` : row.source}
                      </bdi>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{row.visitors}</td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {row.returned_within_7_days.count}
                    </td>
                    <td
                      data-testid="traffic-signups"
                      className="px-3 py-2 text-end font-medium tabular-nums"
                    >
                      {row.signups.count}
                    </td>
                  </tr>
                ))}
                {rows.length > 1 && (
                  <tr className="text-muted">
                    <td className="px-3 py-2">{t.traffic.total}</td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {sum(rows, (r) => r.visitors)}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {sum(rows, (r) => r.returned_within_7_days.count)}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {sum(rows, (r) => r.signups.count)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        ))}
      </div>

      {cohorts.data && <Cohorts rows={cohorts.data.items} />}
    </main>
  );
}

/**
 * Cohorts is each sign-up week by source (ADR-0190) with the median reading
 * session of its readers (requirement 0146). The rest of a cohort's row is
 * read with `sl api`.
 */
export function Cohorts({ rows }: { rows: CohortRow[] }) {
  const t = useT();
  return (
    <section data-testid="traffic-cohorts" className="mt-10">
      <h2 className="text-sm font-semibold">{t.traffic.cohortsTitle}</h2>
      <p className="mt-1 mb-2 text-xs text-muted">{t.traffic.cohortsWhat}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted">{t.traffic.cohortsEmpty}</p>
      ) : (
        <table className="w-full rounded-card border border-line bg-surface text-sm">
          <thead>
            <tr className="border-b border-line text-xs text-muted">
              <th className="px-3 py-2 text-start font-medium">{t.traffic.week}</th>
              <th className="px-3 py-2 text-start font-medium">{t.traffic.source}</th>
              <th className="px-3 py-2 text-end font-medium">{t.traffic.signups}</th>
              <th className="px-3 py-2 text-end font-medium">{t.traffic.medianSession}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row) => (
              <tr key={`${row.week}/${row.source}`} data-testid="traffic-cohort">
                <td className="px-3 py-2 tabular-nums">
                  <bdi>{row.week}</bdi>
                </td>
                <td className="max-w-0 truncate px-3 py-2">
                  <bdi>{row.source}</bdi>
                </td>
                <td className="px-3 py-2 text-end tabular-nums">{row.signups}</td>
                <td
                  data-testid="traffic-cohort-session"
                  className="px-3 py-2 text-end tabular-nums"
                >
                  {row.median_session_seconds === null
                    ? "—"
                    : t.admin.duration(row.median_session_seconds)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** The server sends rows newest day first, busiest source first; keep that order. */
function groupByDay(items: FunnelRow[]): [string, FunnelRow[]][] {
  const out = new Map<string, FunnelRow[]>();
  for (const item of items) {
    out.set(item.day, [...(out.get(item.day) ?? []), item]);
  }
  return [...out.entries()];
}

function sum(rows: FunnelRow[], of: (row: FunnelRow) => number): number {
  return rows.reduce((total, row) => total + of(row), 0);
}

/** A UTC day, named as a date and never shifted into the viewer's timezone. */
function formatDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
